<script>
import { defineComponent, computed, ref, watch } from 'vue'

/**
 * Advisory JD readiness result (PD-1250). Purely presentational — driven by the
 * consolidated shape from useDocReadinessCheck: one row per factor with a weight,
 * a description and a green/amber/red coverage indicator. Never blocks the flow.
 *
 * Three states, matching the design (lvl/wizard.jsx · LvlReadinessCard):
 *  · checking — spinner + expectation-setting message while any factor is pending,
 *               over the table so it can be watched filling in live
 *  · result   — verdict line over the per-factor table, with an (i) popover
 *               carrying each factor's definition
 * The table is open from the start in both; the verdict line stays a toggle so
 * the detail can be dismissed.
 *  · error    — a quiet static line; the flow always stays open
 */
export default defineComponent({
  name: 'ReadinessPanel',
  props: {
    error: { type: String, default: null },
    summary: { type: String, default: '' },
    results: { type: Array, default: () => [] },
  },
  setup(props) {
    // Expanded by default, including mid-run — the table is the point of the
    // panel, and watching the factor groups land as their executions return is
    // what makes the ~20s wait legible. Still a toggle: once the user collapses
    // it, nothing here opens it back up.
    const expanded = ref(true)
    // Id of the factor whose definition popover is open (one at a time).
    const openInfo = ref(null)

    const running = computed(() => props.results.some((r) => r.status === 'pending'))
    const settledCount = computed(() => props.results.filter((r) => r.status !== 'pending').length)

    // Heaviest factor groups first, contextual (zero-weight) ones last.
    const sortedResults = computed(() =>
      props.results.slice().sort((a, b) => (b.weighting || 0) - (a.weighting || 0))
    )

    // A weighted factor with no evidence at all is what the user needs to act
    // on, so the verdict line carries the alert icon rather than the check icon.
    const hasGaps = computed(() =>
      props.results.some((r) => (r.weighting || 0) > 0 && r.coverage && r.coverage.level === 'missing')
    )

    const coverageClass = (row) => {
      if (row.status === 'pending') return 'is-pending'
      const level = row.coverage ? row.coverage.level : 'error'
      if (level === 'sufficient') return 'is-ok'
      if (level === 'thin') return 'is-warn'
      return 'is-miss'
    }

    const coverageLabel = (row) => {
      if (row.status === 'pending') return 'Checking…'
      return row.coverage ? row.coverage.label : 'Not checked'
    }

    // A fresh run drops any open popover, so the previous document's factor
    // detail can't linger over the new result. The expanded/collapsed state is
    // deliberately left alone — it belongs to the user once they've touched it.
    watch(running, (isRunning) => {
      if (isRunning) openInfo.value = null
    })

    return {
      expanded,
      openInfo,
      running,
      settledCount,
      sortedResults,
      hasGaps,
      coverageClass,
      coverageLabel,
    }
  },
})
</script>

<template>
  <div v-if="results.length || error" class="lvl-ready" :class="{ 'is-checking': running }">
    <!-- Error (advisory — never blocks) -->
    <div v-if="error && !results.length" class="lvl-ready-line lvl-ready-line--static">
      <span class="lvl-ready-ic"><span class="ms" style="font-size:17px">rule</span></span>
      <span class="lvl-ready-copy"><strong>Readiness check:</strong> {{ error }} You can still proceed.</span>
    </div>

    <!-- Checking and result share one collapsible line: the chain runs per factor,
         so the table is worth opening mid-run to watch the groups land. -->
    <template v-else>
      <button
        type="button"
        class="lvl-ready-line"
        :aria-expanded="expanded ? 'true' : 'false'"
        @click="expanded = !expanded"
      >
        <span class="lvl-ready-ic">
          <span v-if="running" class="ms" style="font-size:17px">progress_activity</span>
          <span v-else class="ms" :class="{ fill: hasGaps }" style="font-size:17px">{{ hasGaps ? 'error' : 'rule' }}</span>
        </span>
        <span v-if="running" class="lvl-ready-copy">
          <strong>Checking your document against every evaluation factor</strong>
          (about 20 seconds) &mdash; so you know you have enough to answer the questions before you
          start, not partway through.
        </span>
        <span v-else class="lvl-ready-copy"><strong>Readiness check:</strong> {{ summary }}</span>
        <span class="ms lvl-ready-chev" :class="{ 'is-open': expanded }" style="font-size:20px">expand_more</span>
      </button>

      <!-- Count the groups off as each execution returns. -->
      <div v-if="running" class="lvl-ready-prog" role="status">
        {{ settledCount }} of {{ results.length }} factor groups checked
      </div>

      <v-expand-transition>
        <div v-show="expanded">
          <table class="lvl-exp-tbl lvl-ready-tbl">
            <thead>
              <tr>
                <th>Factor group</th>
                <th>Weight</th>
                <th>Coverage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in sortedResults" :key="f.id">
                <td>
                  {{ f.name }}
                  <!-- Factor definition on demand — the same (i) popover the
                       factor questions use, so "what does this cover?" is
                       answerable without leaving the document step. -->
                  <span v-if="f.description" class="lvl-ghead-info lvl-suminfo">
                    <button
                      type="button"
                      class="lvl-ghead-ibtn"
                      :class="{ on: openInfo === f.id }"
                      :aria-expanded="openInfo === f.id ? 'true' : 'false'"
                      :aria-label="'About ' + f.name"
                      title="What this factor covers"
                      @click="openInfo = openInfo === f.id ? null : f.id"
                    >
                      <span class="ms" style="font-size:15px">info</span>
                    </button>
                    <template v-if="openInfo === f.id">
                      <div class="lvl-help-catch" @click="openInfo = null"></div>
                      <div class="lvl-ghead-pop" role="tooltip">
                        <span class="lvl-ghead-pop-title">{{ f.name }}</span>
                        <p>{{ f.description }}</p>
                      </div>
                    </template>
                  </span>
                </td>
                <td>{{ f.weightLabel }}</td>
                <td>
                  <span class="lvl-ready-st" :class="coverageClass(f)">
                    <span class="dot"></span>{{ coverageLabel(f) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="lvl-ready-foot">
            Advisory only &mdash; the check never blocks an evaluation. The assessment stays yours.
          </div>
        </div>
      </v-expand-transition>

      <!-- Feedback on the check itself (PD-1289). Offered once every factor has
           settled — there's nothing to judge mid-run — and outside the
           collapsible section so it's asked whether or not the table is open.
           The host owns it (and the context ids it records); the panel only
           gives it a place to sit. -->
      <div v-if="!running && ($slots.feedback || $scopedSlots.feedback)" class="lvl-ready-fb">
        <slot name="feedback"></slot>
      </div>
    </template>
  </div>
</template>
